import { trackPromise } from 'react-promise-tracker';

import axios from 'axios';
import isEmpty from 'lodash-es/isEmpty';

import {
  clearAccessTokens,
  getAccessToken,
  getRefreshToken,
  getSelectMenu,
  saveAccessTokens,
} from '@ecp/common/src/utils/utils';

import { showExternalAlertDialog } from '@bo-hooks/common/useModal';
import authApi from '@bo-apis/common/authApi';
import { isPODomain } from '@bo-const/common/domain';

export const BASE_URL = process.env.REACT_APP_BASE_URL;
export const DEFAULT_VALID_STATUS_CODE = [200, 201, 202, 204];
export const DEFAULT_ERROR_STATUS_CODE = [400, 404, 500];
export const AUTH_ERROR_STATUS_CODE = [401];

const headers = {
  Accept: 'application/json',
  withCredentials: false,
};

const axiosClient = axios.create({
  baseURL: BASE_URL,
  headers,
});

const axiosSkipErrorClient = axios.create({
  baseURL: BASE_URL,
  headers,
});

export const axiosRawClient = axios.create({
  baseURL: BASE_URL,
  headers,
});

const moveLogoutPage = () => {
  clearAccessTokens();
  window.location.replace('https://www.samsung.net');
};

const movePOLogoutPage = () => {
  clearAccessTokens();
  window.location.replace('/partner/logout');
};

const moveUnauthorizedPage = () => {
  window.location.replace('/unauthorized');
};

const moveErrorPage = () => {
  window.location.replace('/error');
};

const requestInterceptor = (config) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  const selectMenu = getSelectMenu();
  if (selectMenu && !isEmpty(selectMenu.menuNumber) && !isEmpty(selectMenu.menuName)) {
    config.headers['x-menu-info'] = encodeURIComponent(`${selectMenu.menuNumber}_${selectMenu.menuName}`);
  }
  return config;
};

const responseErrorInterceptor = async (error) => {
  if (error?.response?.status === undefined) {
    moveErrorPage();
  } else if (DEFAULT_ERROR_STATUS_CODE.includes(error?.response?.status)) {
    // 400, 404, 500
    error?.response?.status === 400 && typeof error?.response?.data === 'string'
      ? showExternalAlertDialog(error?.response?.data)
      : moveErrorPage();
  } else if (AUTH_ERROR_STATUS_CODE.includes(error?.response?.status)) {
    //401
    if (error?.response?.data?.errorCode === 'TOKEN_EXPIRED') {
      const refreshToken = getRefreshToken();
      try {
        const response = await authApi.reissue(refreshToken);
        const { data: { result: { accessToken: newAccessToken, refreshToken: newRefreshToken } } = {} } = response;
        if (newAccessToken && newRefreshToken) {
          saveAccessTokens(newAccessToken, newRefreshToken);
        } else {
          if (isPODomain()) movePOLogoutPage();
          else {
            clearAccessTokens();
            moveUnauthorizedPage();
          }
        }
        //Retry Origin Request.
        const originRequest = error?.config;
        originRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        //Resolve Origin Request.
        const originalRes = await axiosClient(originRequest);
        return Promise.resolve(originalRes);
      } catch (e) {
        if (isPODomain()) movePOLogoutPage();
        else {
          clearAccessTokens();
          moveUnauthorizedPage();
        }
        // showAlertDialog(
        //   e?.response?.data?.errorCode === 'TOKEN_EXPIRED'
        //     ? '세션만료되어 로그아웃되었습니다'
        //     : '알 수 없는 오류로 로그아웃되었습니다',
        //   () => {
        //     moveLogoutPage();
        //   }
        // );
      }
    } else if (error?.response?.data?.errorCode === 'INVALID_TOKEN') {
      if (isPODomain()) {
        movePOLogoutPage();
      } else {
        moveLogoutPage();
      }
    } else if (error?.response?.data?.errorCode === 'INVALID_SESSION') {
      clearAccessTokens();
      window.location.replace('/');
    } else {
      // 권한없는 페이지 접근하는 경우에는 토큰을 날리지 않는다.
      moveUnauthorizedPage();
    }
  } else {
    moveErrorPage();
  }
  return Promise.reject(error);
};

axiosClient.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));
axiosClient.interceptors.response.use((response) => response.data, responseErrorInterceptor);
axiosSkipErrorClient.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));

const validStatusConfig = (validStatusList) => ({
  validateStatus: (status) => {
    if (validStatusList.length < 1) {
      return true;
    }
    return validStatusList.includes(status);
  },
});

export const getRequest = (options, skip = {}) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        method: 'GET',
      })
    : axiosClient({
        ...options,
        method: 'GET',
      });
  return skipSpinner ? client : trackPromise(client);
};

export const postRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'POST',
      })
    : axiosClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'POST',
      });
  return skipSpinner ? client : trackPromise(client);
};

export const putRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'PUT',
      })
    : axiosClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'PUT',
      });
  return skipSpinner ? client : trackPromise(client);
};

export const patchRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'PATCH',
      })
    : axiosClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'PATCH',
      });
  return skipSpinner ? client : trackPromise(client);
};

export const deleteRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'DELETE',
      })
    : axiosClient({
        ...options,
        ...validStatusConfig(validStatusList),
        method: 'DELETE',
      });
  return skipSpinner ? client : trackPromise(client);
};
